/**
 * Mirror of Store TrackerHelper#tracking_url_from_carrier / get_matched.
 * Handles prod-ish Omni carrier strings such as `ups_mi_flat`,
 * `usps ground advantage`, and `… (ups mail innovations)`.
 * Returns null when carrier or tracking code cannot be mapped.
 */
export const getMatchedCarrier = carrier => {
  const normalized = String(carrier || "").toLowerCase()
  if (!normalized) {
    return null
  }
  if (normalized.includes("mail innovations")) {
    return "ups"
  }
  // ups_mi before ups so ups_mi_flat → ups_mi (Accuzip / Mail Innovations tracker).
  // usps before ups so usps strings → usps, not ups.
  const match = normalized.match(/fedex|ups_mi|usps|ups|dhl/)
  return match ? match[0] : null
}

/**
 * Drop an Intelligent Mail Package Barcode postal-routing head
 * (`420` + ZIP5, optionally ZIP+4). Carriers track the payload that
 * follows (typically a 91–95 channel id such as `9261…`), not the
 * routing prefix — USPS returns "Tracking Not Available" for the full
 * string.
 */
export const stripImPbRoutingPrefix = trackingCode => {
  const match = String(trackingCode).match(/^420(\d{5})(?:\d{4})?(9[1-5]\d+)$/)
  return match ? match[2] : trackingCode
}

/**
 * Omni/GiC kit rows sometimes append `#` to tracking numbers.
 * Also strips IMpb `420`+ZIP routing so carrier links resolve.
 */
export const normalizeTrackingCode = trackingCode => {
  if (trackingCode == null) {
    return null
  }
  const trimmed = String(trackingCode).trim().replace(/#+$/, "")
  if (trimmed === "") {
    return null
  }
  const withoutRouting = stripImPbRoutingPrefix(trimmed)
  return withoutRouting === "" ? null : withoutRouting
}

const carrierUrlBuilders = {
  dhl: trackingCode =>
    `https://www.dhl.com/us-en/home/tracking/tracking-global-forwarding.html?submit=1&tracking-id=${trackingCode}`,
  ups: trackingCode =>
    `https://wwwapps.ups.com/WebTracking/track?track=yes&trackNums=${trackingCode}`,
  ups_mi: trackingCode =>
    `https://upsmi.trackmypiece.com/?track=${trackingCode}`,
  usps: trackingCode =>
    `https://tools.usps.com/go/TrackConfirmAction.action?tRef=fullpage&tLc=1&text28777=&tLabels=${trackingCode}`,
  fedex: trackingCode =>
    `https://www.fedex.com/apps/fedextrack/?action=track&trackingnumber=${trackingCode}`,
}

export const trackingUrlFromCarrier = (carrier, trackingCode) => {
  const code = normalizeTrackingCode(trackingCode)
  if (!code) {
    return null
  }
  const matched = getMatchedCarrier(carrier)
  if (!matched || !carrierUrlBuilders[matched]) {
    return null
  }
  return carrierUrlBuilders[matched](code)
}

export const formatCarrierLabel = carrier => {
  const matched = getMatchedCarrier(carrier)
  if (!matched) {
    return carrier || null
  }
  return matched.replace(/_/g, " ").toUpperCase()
}
